import type { Message, User } from '@graphql/generated';

const extractName = (str: string) => {
  const addedMatch = str.match(/^(.*?) has added (.*)$/);
  if (addedMatch) {
    return [addedMatch[1], addedMatch[2]];
  }
  const createdMatch = str.match(/^(.*?) has created this group$/);
  if (createdMatch) {
    return [createdMatch[1]];
  }
  return [];
};

const compareByCreatedAtThenIdDesc = <T extends { createdAt: any; id: any }>(
  a: T,
  b: T
): number => {
  const ta = new Date(a.createdAt).getTime();
  const tb = new Date(b.createdAt).getTime();
  const diff = tb - ta;
  if (diff !== 0) return diff;

  const aid = Number(a.id);
  const bid = Number(b.id);
  if (!Number.isNaN(aid) && !Number.isNaN(bid)) return bid - aid;
  return String(b.id).localeCompare(String(a.id));
};

// The timestamp a message sorts and sections by: publishedAt, else createdAt.
// `||`, not `??`: an unsent draft carries `publishedAt: false`, which `??`
// keeps, and new Date(false) is 1970 — the draft sorted as the oldest
// message, at the top of the thread instead of the bottom (TA-11065).
const messageDisplayTime = (
  message: Pick<Message, 'publishedAt' | 'createdAt'>
) => message.publishedAt || message.createdAt;

// Propagates read receipts from newer to older sender messages.
// The list must be sorted newest-first (index 0 = newest). If a reader X is
// present on message[i], all older isSender messages[i+1..n] with empty
// readReceipts are updated to also show X — matching the invariant that reading
// message N implies all prior messages were read.
const propagateReadReceipts = (
  messageList: (Message | string)[]
): (Message | string)[] => {
  const seenReaders: User[] = [];

  return messageList.map((item) => {
    if (typeof item === 'string') return item;

    const receipts = item.readReceipts ?? [];
    for (const user of receipts) {
      if (!seenReaders.some((u) => u.id === user.id)) {
        seenReaders.push(user);
      }
    }

    if (item.isSender && seenReaders.length > 0 && receipts.length === 0) {
      return { ...item, readReceipts: [...seenReaders] };
    }

    return item;
  });
};

// Single read-receipt source for a selected message. The Apollo cache entry is
// preferred because subscriptions keep it fresh, but older messages can have
// empty readReceipts in the cache while the rendered row shows them as read via
// propagateReadReceipts — in that case fall back to the rendered message so the
// Read icon, the "Read by" menu option, and the Read by modal never disagree.
const resolveReadReceipts = (
  cacheMessage: Pick<Message, 'readReceipts'> | null | undefined,
  renderedMessage: Pick<Message, 'readReceipts'> | null | undefined
): User[] => {
  const cached = cacheMessage?.readReceipts ?? [];
  if (cached.length > 0) return cached;
  return renderedMessage?.readReceipts ?? [];
};

export {
  extractName,
  compareByCreatedAtThenIdDesc,
  messageDisplayTime,
  propagateReadReceipts,
  resolveReadReceipts,
};
